import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  loadAssessment,
  submitIdentity,
  verifyAssessmentOtp,
  assessmentConsent,
  startAssessment,
  getAssessmentStatus,
  assessmentHeartbeat,
  saveAssessmentAnswer,
  submitAssessment,
  saveAssessmentPhoto,
} from "../../services/assessmentCalls";
import s from "./CandidateAssessment.module.css";
import { poweredByUrl } from "./poweredBy";
import { displayRole } from "../PartnerPortal/partnerRoleCatalog";

/* Candidate test-taking portal for API-invited candidates.
   Flow: load → welcome → identity → OTP → consent → photo → timed test → done.
   White-labelled by the partner's brand; powered by AwaSource.

   Anti-faking (invite-only assessments only — never the Talent onboarding
   test): a single active session per attempt, blocked copy/paste and
   right-click, tab/fullscreen-exit detection, a best-effort devtools
   heuristic, one start-of-test photo for human review, and per-question
   timing. All of it is either a session-integrity control (blocking) or an
   informational signal surfaced to the partner's reviewer — none of it
   auto-fails a candidate. */

const NAVY = "#0d1b3e";
const HEARTBEAT_MS = 20000;
const DEVTOOLS_GAP_PX = 160;
const TOKEN_KEY = (id) => `pa_tok_${id}`;
const DEVICE_KEY = (id) => `pa_dtok_${id}`;

/** [3] → "3", [3, 7] → "3 and 7", [2, 3, 7] → "2, 3 and 7". */
const listQuestions = (nums) => (nums.length < 2 ? String(nums[0]) : `${nums.slice(0, -1).join(", ")} and ${nums[nums.length - 1]}`);

function newDeviceToken() {
  try {
    return crypto.randomUUID();
  } catch {
    return `dt_${Date.now()}_${Math.random().toString(16).slice(2)}`;
  }
}

export default function CandidateAssessment() {
  const { sessionId } = useParams();
  const [phase, setPhase] = useState("loading"); // loading|welcome|identity|otp|consent|photo|test|timeup|submitError|done|closed|locked|error
  const [closedReason, setClosedReason] = useState(null); // time_up|invite_expired
  const [timedOut, setTimedOut] = useState(false);
  const [view, setView] = useState(null); // brand/role/questionCount/candidate/limitSeconds/status
  const [error, setError] = useState("");
  const [warning, setWarning] = useState("");

  const [form, setForm] = useState({ firstName: "", lastName: "", email: "" });
  const [otp, setOtp] = useState("");
  const [token, setToken] = useState("");
  const [consentChecked, setConsentChecked] = useState(false);

  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [idx, setIdx] = useState(0);
  const [confirmSubmit, setConfirmSubmit] = useState(false);
  const [limit, setLimit] = useState(1200);
  const [timeLeft, setTimeLeft] = useState(1200);
  const [busy, setBusy] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null); // captured still, data URL
  const [cameraError, setCameraError] = useState("");
  const timerRef = useRef(null);
  const heartbeatRef = useRef(null);
  const devtoolsRef = useRef(null);
  const warnTimerRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const questionStartRef = useRef(Date.now());
  const deviceTokenRef = useRef("");
  // The countdown and heartbeat callbacks outlive the render that created
  // them, so submit reads the latest answers and token from refs.
  const answersRef = useRef(answers);
  const questionsRef = useRef(questions);
  const tokenRef = useRef(token);
  answersRef.current = answers;
  questionsRef.current = questions;
  tokenRef.current = token;
  const submittingRef = useRef(false);
  // Answer saves run one at a time, and submit waits for the last one, so
  // two writes to the same session never race on the server.
  const saveChainRef = useRef(Promise.resolve());
  const savedRef = useRef({}); // questionId → the response last sent to the server

  // Cumulative anti-faking counters. A plain ref (not state) — these are
  // write-many, read-rarely, and must never drive re-renders on their own.
  const integrityRef = useRef({
    pastedAttempts: 0,
    pastedQuestionIds: [],
    tabBlurCount: 0,
    tabBlurDurationMs: 0,
    fullscreenExitCount: 0,
    devtoolsDetectedCount: 0,
    rightClickAttempts: 0,
  });
  const blurStartRef = useRef(null);

  const flash = (msg) => {
    setWarning(msg);
    if (warnTimerRef.current) clearTimeout(warnTimerRef.current);
    warnTimerRef.current = setTimeout(() => setWarning(""), 3200);
  };

  // ---- load + resume ----
  useEffect(() => {
    deviceTokenRef.current = sessionStorage.getItem(DEVICE_KEY(sessionId)) || newDeviceToken();
    sessionStorage.setItem(DEVICE_KEY(sessionId), deviceTokenRef.current);

    const storedToken = sessionStorage.getItem(TOKEN_KEY(sessionId));

    loadAssessment(sessionId)
      .then(async (res) => {
        const v = res?.data || res;
        setView(v);
        setForm((f) => ({ ...f, firstName: v?.candidate?.firstName || "" }));

        if (showClosedState(v)) return;

        if (v.status === "in_progress" && storedToken) {
          try {
            const statusRes = await getAssessmentStatus(sessionId, storedToken);
            const st = statusRes?.data || statusRes;
            setToken(storedToken);
            setQuestions(st.questions || []);
            setAnswers(st.answers || {});
            savedRef.current = { ...(st.answers || {}) };
            const lim = st.remainingSeconds ?? v.limitSeconds ?? 1200;
            setLimit(v.limitSeconds || 1200);
            setTimeLeft(lim);
            const firstUnanswered = (st.questions || []).findIndex((q) => !st.answers?.[q.id]);
            setIdx(firstUnanswered === -1 ? Math.max(0, (st.questions || []).length - 1) : firstUnanswered);
            questionStartRef.current = Date.now();
            return setPhase("test");
          } catch {
            // Stored token no longer valid for this session — fall through
            // to the normal identity flow rather than getting stuck.
          }
        }
        setPhase("welcome");
      })
      .catch((e) => {
        setError(e?.response?.data?.errors || "This assessment link is invalid or has expired.");
        setPhase("error");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  // ---- countdown ----
  useEffect(() => {
    if (phase !== "test") return;
    timerRef.current = setInterval(() => setTimeLeft((t) => Math.max(0, t - 1)), 1000);
    return () => clearInterval(timerRef.current);
  }, [phase]);

  useEffect(() => {
    if (phase === "test" && timeLeft <= 0) doSubmit(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, timeLeft]);

  // ---- single-active-session heartbeat ----
  useEffect(() => {
    if (phase !== "test") return;
    heartbeatRef.current = setInterval(async () => {
      try {
        await assessmentHeartbeat(sessionId, token, deviceTokenRef.current);
      } catch (e) {
        if (e?.response?.status === 409) {
          clearInterval(heartbeatRef.current);
          clearInterval(timerRef.current);
          setPhase("locked");
        } else if (e?.response?.status === 410) {
          // The server already closed the attempt because its time ran out.
          stopTest();
          refreshFinalState();
        }
      }
    }, HEARTBEAT_MS);
    return () => clearInterval(heartbeatRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, sessionId, token]);

  // ---- tab-blur / navigate-away detection ----
  useEffect(() => {
    if (phase !== "test") return;
    const onVisibility = () => {
      if (document.hidden) {
        blurStartRef.current = Date.now();
        integrityRef.current.tabBlurCount += 1;
      } else if (blurStartRef.current) {
        integrityRef.current.tabBlurDurationMs += Date.now() - blurStartRef.current;
        blurStartRef.current = null;
        flash("You navigated away from the test — this has been noted.");
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [phase]);

  // ---- fullscreen exit detection ----
  useEffect(() => {
    if (phase !== "test") return;
    const onFsChange = () => {
      if (!document.fullscreenElement) {
        integrityRef.current.fullscreenExitCount += 1;
        flash("Please stay in fullscreen for the rest of the assessment.");
      }
    };
    document.addEventListener("fullscreenchange", onFsChange);
    return () => document.removeEventListener("fullscreenchange", onFsChange);
  }, [phase]);

  // ---- devtools heuristic (best-effort; window-size gap) ----
  useEffect(() => {
    if (phase !== "test") return;
    let wasOpen = false;
    devtoolsRef.current = setInterval(() => {
      // Only meaningful in fullscreen: in a normal window, browser zoom and
      // side panels open the same gap without any devtools.
      const open =
        Boolean(document.fullscreenElement) &&
        (window.outerWidth - window.innerWidth > DEVTOOLS_GAP_PX ||
          window.outerHeight - window.innerHeight > DEVTOOLS_GAP_PX);
      if (open && !wasOpen) {
        integrityRef.current.devtoolsDetectedCount += 1;
      }
      wasOpen = open;
    }, 1500);
    return () => clearInterval(devtoolsRef.current);
  }, [phase]);

  // ---- block copy/paste + right-click during the test ----
  useEffect(() => {
    if (phase !== "test") return;
    const recordPasteAttempt = () => {
      const qid = questions[idx]?.id;
      integrityRef.current.pastedAttempts += 1;
      if (qid && !integrityRef.current.pastedQuestionIds.includes(qid)) {
        integrityRef.current.pastedQuestionIds.push(qid);
      }
    };
    const onPaste = (e) => {
      e.preventDefault();
      recordPasteAttempt();
      flash("Pasting is disabled for this assessment — please type your response.");
    };
    const onCopyOrCut = (e) => {
      e.preventDefault();
      flash("Copying is disabled for this assessment — please type your response.");
    };
    const onDragOver = (e) => {
      const types = e.dataTransfer?.types ? Array.from(e.dataTransfer.types) : [];
      if (types.includes("text/plain") || types.includes("text/html")) e.preventDefault();
    };
    const onDrop = (e) => {
      const text = e.dataTransfer?.getData("text") || "";
      if (!text) return;
      e.preventDefault();
      recordPasteAttempt();
      flash("Pasting is disabled for this assessment — please type your response.");
    };
    const onContextMenu = (e) => {
      e.preventDefault();
      integrityRef.current.rightClickAttempts += 1;
    };
    document.addEventListener("paste", onPaste, true);
    document.addEventListener("copy", onCopyOrCut, true);
    document.addEventListener("cut", onCopyOrCut, true);
    document.addEventListener("dragover", onDragOver, true);
    document.addEventListener("drop", onDrop, true);
    document.addEventListener("contextmenu", onContextMenu);
    return () => {
      document.removeEventListener("paste", onPaste, true);
      document.removeEventListener("copy", onCopyOrCut, true);
      document.removeEventListener("cut", onCopyOrCut, true);
      document.removeEventListener("dragover", onDragOver, true);
      document.removeEventListener("drop", onDrop, true);
      document.removeEventListener("contextmenu", onContextMenu);
    };
  }, [phase, questions, idx]);

  // ---- camera preview lifecycle for the photo step ----
  useEffect(() => {
    if (phase !== "photo") return;
    let cancelled = false;
    setCameraError("");
    navigator.mediaDevices
      ?.getUserMedia({ video: { width: 320, height: 240 } })
      .then((stream) => {
        if (cancelled) return stream.getTracks().forEach((t) => t.stop());
        streamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;
      })
      .catch(() => setCameraError("Camera unavailable — you can continue without a photo."));
    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    };
  }, [phase]);

  useEffect(() => () => clearTimeout(warnTimerRef.current), []);

  const brand = view?.brand || { name: "Assessment", brandColor: NAVY };
  const roleName = displayRole(view?.role);

  // ---- actions ----
  const sendCode = async () => {
    setError("");
    const details = { firstName: form.firstName.trim(), lastName: form.lastName.trim(), email: form.email.trim() };
    if (!details.firstName || !details.email.includes("@")) return setError("Enter your name and a valid email.");
    setBusy(true);
    try {
      await submitIdentity(sessionId, details);
      setPhase("otp");
    } catch (e) {
      setError(e?.response?.data?.errors || "Couldn't send the code. Check your email and try again.");
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    setError("");
    if (otp.length < 6) return setError("Enter the 6-digit code.");
    setBusy(true);
    try {
      const res = await verifyAssessmentOtp(sessionId, otp);
      const tk = (res?.data || res)?.accessToken;
      setToken(tk);
      sessionStorage.setItem(TOKEN_KEY(sessionId), tk);
      setPhase("consent");
    } catch (e) {
      setError(e?.response?.data?.errors || "Incorrect or expired code.");
    } finally {
      setBusy(false);
    }
  };

  const proceedToPhoto = async () => {
    setError("");
    setBusy(true);
    try {
      await assessmentConsent(sessionId, token);
      setPhase("photo");
    } catch (e) {
      setError(e?.response?.data?.errors || "Couldn't continue. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const capturePhoto = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
    setPhotoPreview(canvas.toDataURL("image/jpeg", 0.8));
    streamRef.current?.getTracks().forEach((t) => t.stop());
  };

  const retakePhoto = () => {
    setPhotoPreview(null);
    setPhase("photo"); // re-trigger the camera effect
  };

  const beginTest = async () => {
    setError("");
    setBusy(true);
    try {
      if (photoPreview) {
        try {
          await saveAssessmentPhoto(sessionId, token, deviceTokenRef.current, photoPreview);
        } catch {
          // Non-fatal — the photo is a courtesy signal, never a gate to taking the test.
        }
      }
      const res = await startAssessment(sessionId, token, deviceTokenRef.current);
      const d = res?.data || res;
      setQuestions(d.questions || []);
      const lim = d.remainingSeconds ?? d.limitSeconds ?? view?.limitSeconds ?? 1200;
      setLimit(d.limitSeconds || view?.limitSeconds || 1200);
      setTimeLeft(lim);
      questionStartRef.current = Date.now();
      document.documentElement.requestFullscreen?.().catch(() => {});
      setPhase("test");
    } catch (e) {
      if (e?.response?.status === 409) setPhase("locked");
      else setError(e?.response?.data?.errors || "Couldn't start the assessment.");
    } finally {
      setBusy(false);
    }
  };

  const setAnswer = (qid, val) => {
    setConfirmSubmit(false);
    setAnswers((a) => ({ ...a, [qid]: val }));
  };
  const unanswered = questions.reduce((list, q, i) => (String(answers[q.id] || "").trim() ? list : [...list, i]), []);

  const persist = (qid) => {
    const response = answersRef.current[qid];
    if (response == null || savedRef.current[qid] === response) return saveChainRef.current;
    const timeMs = Date.now() - questionStartRef.current;
    savedRef.current[qid] = response;
    saveChainRef.current = saveChainRef.current
      .then(() =>
        saveAssessmentAnswer(sessionId, tokenRef.current, deviceTokenRef.current, qid, response, { ...integrityRef.current }, timeMs)
      )
      .catch(() => {
        if (savedRef.current[qid] === response) delete savedRef.current[qid];
      });
    return saveChainRef.current;
  };

  const next = () => {
    const q = questions[idx];
    persist(q.id);
    questionStartRef.current = Date.now();
    if (idx < questions.length - 1) setIdx(idx + 1);
    else if (unanswered.length) setConfirmSubmit(true);
    else doSubmit(false);
  };
  const back = () => {
    if (idx <= 0) return;
    questionStartRef.current = Date.now();
    setConfirmSubmit(false);
    setIdx(idx - 1);
  };
  const goTo = (i) => {
    persist(questions[idx].id);
    questionStartRef.current = Date.now();
    setConfirmSubmit(false);
    setIdx(i);
  };

  const stopTest = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (heartbeatRef.current) clearInterval(heartbeatRef.current);
    if (devtoolsRef.current) clearInterval(devtoolsRef.current);
  };

  const leaveTest = () => {
    sessionStorage.removeItem(TOKEN_KEY(sessionId));
    sessionStorage.removeItem(DEVICE_KEY(sessionId));
    if (document.fullscreenElement) document.exitFullscreen?.().catch(() => {});
  };

  /** Shows the finished/closed screen for a session that can't be taken any more. Returns true if it did. */
  const showClosedState = (v) => {
    if (v?.status === "completed") {
      setTimedOut(Boolean(v.timedOut));
      setPhase("done");
      return true;
    }
    if (["expired", "canceled"].includes(v?.status)) {
      setClosedReason(v.closedReason || null);
      setPhase("closed");
      return true;
    }
    return false;
  };

  const refreshFinalState = async () => {
    leaveTest();
    try {
      const res = await loadAssessment(sessionId);
      const v = res?.data || res;
      setView(v);
      if (!showClosedState(v)) setPhase("submitError");
    } catch {
      setPhase("submitError");
    }
  };

  const doSubmit = async (auto) => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setBusy(true);
    setError("");
    stopTest();
    if (auto) setPhase("timeup");
    const payload = questionsRef.current.map((q) => ({ questionId: q.id, response: answersRef.current[q.id] || "" }));
    try {
      await saveChainRef.current;
      const res = await submitAssessment(sessionId, tokenRef.current, deviceTokenRef.current, payload, { ...integrityRef.current });
      setTimedOut(Boolean((res?.data || res)?.timedOut));
      leaveTest();
      setPhase("done");
    } catch (e) {
      const status = e?.response?.status;
      if (status === 409) setPhase("locked");
      else if (status === 410) await refreshFinalState();
      else {
        setError(e?.response?.data?.message || "We couldn't reach the server.");
        setPhase("submitError");
      }
    } finally {
      submittingRef.current = false;
      setBusy(false);
    }
  };

  const mm = String(Math.floor(timeLeft / 60));
  const ss = String(timeLeft % 60).padStart(2, "0");
  const pct = questions.length ? Math.round((idx / questions.length) * 100) : 0;

  // ---- render ----
  return (
    <div className={s.body}>
      <div className={s.appbar}>
        <div className={s.brandWrap}>
          <div className={s.brandMark} style={{ background: brand.brandColor || NAVY }}>
            {brand.logoUrl ? <img src={brand.logoUrl} alt="" className={s.logoImg} /> : (brand.name || "A").slice(0, 2).toUpperCase()}
          </div>
          <span className={s.brandName}>{brand.name}</span>
        </div>
        {/* Not a link while the clock runs: opening it hides the test page,
            which is recorded as leaving the test. */}
        {phase === "test" || phase === "timeup" ? (
          <span className={s.powered}>Powered by <b className={s.poweredBrand}>AwaSource</b></span>
        ) : (
          <a
            className={`${s.powered} ${s.poweredLink}`}
            href={poweredByUrl("assessment", view?.brand?.name)}
            target="_blank"
            rel="noopener noreferrer"
          >
            Powered by <b className={s.poweredBrand}>AwaSource</b>
          </a>
        )}
      </div>

      <div className={s.wrap}>
        {phase === "loading" && <p className={s.sub}>Loading your assessment…</p>}
        {phase === "error" && <Card><p className={`${s.sub} ${s.subError}`}>{error}</p></Card>}
        {phase === "closed" &&
          (closedReason === "time_up" ? (
            <Card>
              <h2 className={s.h1}>Your time ran out</h2>
              <p className={s.sub}>The time limit passed before any answers were saved, so there was nothing to submit. Contact {brand.name} if you'd like another attempt.</p>
            </Card>
          ) : (
            <Card>
              <h2 className={s.h1}>This invitation has expired</h2>
              <p className={s.sub}>This assessment can no longer be started. Contact {brand.name} for a new invitation.</p>
            </Card>
          ))}

        {phase === "timeup" && (
          <Card center>
            <h2 className={s.h1}>Time's up</h2>
            <p className={s.sub}>Submitting your answers…</p>
          </Card>
        )}

        {phase === "submitError" && (
          <Card>
            <h2 className={s.h2}>Your answers haven't been submitted yet</h2>
            {error && <Err>{error}</Err>}
            <p className={s.sub}>Check your connection and try again. Don't close this window until you see the confirmation.</p>
            <button className={`${s.btnAmber} ${s.btnFull}`} onClick={() => doSubmit(false)} disabled={busy}>{busy ? "Submitting…" : "Try again"}</button>
          </Card>
        )}

        {phase === "welcome" && (
          <div>
            <span className={s.eyebrow}>Psychometric assessment</span>
            <h1 className={s.h1}>Show {brand.name} how you think under pressure.</h1>
            <p className={s.sub}>A short set of real-world scenario questions. There are no trick answers — respond how you naturally would.</p>
            <div className={s.hero}>
              <div className={s.heroLabel}>Your assessment</div>
              <div className={s.heroRole}>{roleName}</div>
              <div className={s.heroMeta}>
                {view?.questionCount} scenarios · ~{Math.round((view?.limitSeconds || 1200) / 60)} minutes · answers save as you go
              </div>
            </div>
            <button className={s.btnAmber} onClick={() => setPhase("identity")}>Get started →</button>
          </div>
        )}

        {phase === "identity" && (
          <Card>
            <h2 className={s.h2}>Before you begin</h2>
            <p className={s.sub}>Confirm your details so {brand.name} can match your results to you.</p>
            {error && <Err>{error}</Err>}
            <div className={s.nameGrid}>
              <input className={s.inp} placeholder="First name" value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} />
              <input className={s.inp} placeholder="Last name" value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} />
            </div>
            <input className={`${s.inp} ${s.mt12}`} placeholder="you@email.com" autoComplete="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <p className={s.hint}>
              {view?.candidate?.email
                ? <>Use the email this invitation was sent to ({view.candidate.email}). We'll send a one-time code there.</>
                : "We'll email you a one-time code to verify it's you."}
            </p>
            <button className={`${s.btnAmber} ${s.btnFull}`} onClick={sendCode} disabled={busy}>{busy ? "Sending…" : "Send code & continue"}</button>
          </Card>
        )}

        {phase === "otp" && (
          <Card>
            <h2 className={s.h2}>Verify your email</h2>
            <p className={s.sub}>Enter the 6-digit code we sent to {view?.candidate?.email || form.email}.</p>
            {error && <Err>{error}</Err>}
            <input className={`${s.inp} ${s.otpInput}`} maxLength={6} inputMode="numeric" placeholder="______" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))} />
            <button className={`${s.btnAmber} ${s.btnFull} ${s.mt14}`} onClick={verify} disabled={busy}>{busy ? "Verifying…" : "Verify & continue"}</button>
          </Card>
        )}

        {phase === "consent" && (
          <Card>
            <h2 className={s.h2}>One quick thing</h2>
            {error && <Err>{error}</Err>}
            <label className={s.consentLabel}>
              <input type="checkbox" checked={consentChecked} onChange={(e) => setConsentChecked(e.target.checked)} className={s.consentCheck} />
              <span>
                I agree that my responses will be processed by AwaSource on behalf of {brand.name} — including automated and
                AI-assisted scoring — to generate assessment insights. To keep this assessment fair, this session is monitored:
                copy/paste is disabled, only one device/tab may be active at a time, and a single photo is captured at the start
                for {brand.name}'s reviewer (never used for automated face matching).
              </span>
            </label>
            <button className={`${s.btnAmber} ${s.btnFull}`} style={{ opacity: consentChecked ? 1 : 0.5 }} disabled={!consentChecked || busy} onClick={proceedToPhoto}>{busy ? "Starting…" : "Continue"}</button>
          </Card>
        )}

        {phase === "photo" && (
          <Card>
            <h2 className={s.h2}>Quick photo</h2>
            <p className={s.sub}>One snapshot for {brand.name}'s reviewer — it's optional, and never run through automated face matching.</p>
            <div className={s.photoBox}>
              {photoPreview ? (
                <img src={photoPreview} alt="Captured" className={s.photoImg} />
              ) : cameraError ? (
                <span className={s.photoHint}>{cameraError}</span>
              ) : (
                <video ref={videoRef} autoPlay playsInline muted className={s.photoVideo} />
              )}
            </div>
            <div className={s.photoActions}>
              {photoPreview ? (
                <>
                  <button className={s.btnGhost} onClick={retakePhoto} disabled={busy}>Retake</button>
                  <button className={`${s.btnAmber} ${s.btnFull}`} onClick={beginTest} disabled={busy}>{busy ? "Starting…" : "Start assessment"}</button>
                </>
              ) : (
                <>
                  {!cameraError && <button className={s.btnGhost} onClick={capturePhoto}>Capture</button>}
                  <button className={`${s.btnAmber} ${s.btnFull}`} onClick={beginTest} disabled={busy}>{busy ? "Starting…" : "Continue without a photo"}</button>
                </>
              )}
            </div>
          </Card>
        )}

        {phase === "test" && questions[idx] && (
          <Card>
            <div className={s.testHead}>
              <span className={s.qtag}>{roleName}</span>
              <span className={`${s.timer} ${timeLeft <= 60 ? s.timerLow : ""}`}>⏱ {mm}:{ss}</span>
            </div>
            <div className={s.progRow}>
              <span className={s.progLabel}>Question {idx + 1} of {questions.length}</span>
              <span className={s.progPct}>{pct}%</span>
            </div>
            <div className={s.track}><div className={s.trackFill} style={{ width: `${pct}%` }} /></div>
            <div className={s.qDots} aria-label="Jump to a question">
              {questions.map((q, i) => {
                const blank = unanswered.includes(i);
                return (
                  <button
                    key={q.id}
                    type="button"
                    className={`${s.qDot} ${blank ? "" : s.qDotDone} ${i === idx ? s.qDotCurrent : ""}`}
                    onClick={() => i !== idx && goTo(i)}
                    disabled={busy}
                    aria-current={i === idx ? "step" : undefined}
                    aria-label={`Question ${i + 1}${blank ? ", not answered" : ", answered"}`}
                  >
                    {i + 1}
                  </button>
                );
              })}
            </div>

            <span className={`${s.qtag} ${s.qtagBlock}`}>{questions[idx].tag}</span>
            <div className={s.scenario}>{questions[idx].scenario}</div>
            <div className={s.respLabel}>Your response</div>
            <textarea
              className={s.ta}
              placeholder="Type how you would actually handle this…"
              value={answers[questions[idx].id] || ""}
              onChange={(e) => setAnswer(questions[idx].id, e.target.value)}
              onBlur={() => persist(questions[idx].id)}
            />
            {confirmSubmit && unanswered.length > 0 ? (
              <div className={s.unansweredBox} role="alert">
                <p className={s.unansweredText}>
                  You haven't answered {unanswered.length === 1 ? "question" : "questions"}{" "}
                  {listQuestions(unanswered.map((i) => i + 1))}. You still have {mm}:{ss} left. The clock keeps running.
                </p>
                <div className={s.navBtns}>
                  <button className={s.btnAmber} onClick={() => goTo(unanswered[0])}>
                    {unanswered[0] === idx ? "Answer" : "Go to"} question {unanswered[0] + 1}
                  </button>
                  <button className={s.btnGhost} onClick={() => doSubmit(false)} disabled={busy}>Submit anyway</button>
                </div>
              </div>
            ) : (
              <div className={s.testFoot}>
                <span className={s.savedNote}>Saved automatically</span>
                <div className={s.navBtns}>
                  {idx > 0 && <button className={s.btnGhost} onClick={back}>← Back</button>}
                  <button className={s.btnAmber} onClick={next} disabled={busy}>{idx === questions.length - 1 ? "Submit" : "Next →"}</button>
                </div>
              </div>
            )}
          </Card>
        )}

        {phase === "done" && (
          <Card center>
            <div className={s.doneIc}>✓</div>
            <h2 className={s.h1}>{timedOut ? "Time's up — your answers are in" : "All done — thank you!"}</h2>
            <p className={s.sub}>
              {timedOut ? "Your time ran out, and the answers you saved have been submitted." : "Your responses have been submitted."}{" "}
              {brand.name} will review your results and be in touch. You can close this window.
            </p>
          </Card>
        )}
      </div>

      {warning && <div className={s.warnToast}>{warning}</div>}

      {phase === "locked" && (
        <div className={s.lockNotice}>
          <div className={s.lockCard}>
            <h2 className={s.h2}>Session opened elsewhere</h2>
            <p className={s.sub}>This assessment is currently open in another window or device. Only one active session is allowed per attempt — close the other tab and reload this page to continue.</p>
          </div>
        </div>
      )}
    </div>
  );
}

function Card({ children, center }) {
  return <div className={`${s.card} ${center ? s.cardCenter : ""}`}>{children}</div>;
}
function Err({ children }) {
  return <div className={s.err}>{children}</div>;
}
